(ns hai.ui.components.collection.pagination
  (:require
   [edn-query-language.core :as eql]
   [hai.ui.components :as c]
   [hai.ui.components.collection.table :as-alias table]
   [hai.ui.components.collection.utils :as utils]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [hai.ui.utils.validate :refer [validate]]
   [humbleai.belt.malli.core :as m]
   [humbleai.belt.malli.transform.translate-keys :as translate-keys]
   [humbleai.belt.vector :refer [remove-elem-at]]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def subquery-ast (eql/query->ast [:total :cursor :prev-cursor :next-cursor]))

(def default-per-page 25)

(def Limit
  [:map
   {:default {}}
   [:table/per-page
    {:default default-per-page}
    :int]])

(def LimitedCollection
  [:map
   [:table/pagination
    Limit]])

(def Sort
  [:map
   [:table/sort-attrs
    {:default []
     ::translate-keys/from :sort-attrs}
    [:vector :any]]
   [:table/direction
    {:default :asc
     ::translate-keys/from :direction}
    [:enum :asc :desc]]])

(def Cursor
  [:or
   nil?
   [:map
    [:cursor/type [:enum :start :end]]
    [:cursor/value [:vector :any]]]])

(def Pagination
  (m/merge
   Limit
   [:map
    {:default {}}
    [:table/cursor
     {:optional true}
     Cursor]
    [:table/next-cursor
     {:optional true}
     Cursor]
    [:table/prev-cursor
     {:optional true}
     Cursor]]))

(defn resolve-cursor
  "FX helper for resolving a page cursor"
  [{:keys [db]} ?table cursor-name]
  (let [{:table/keys [id] :as table} (utils/->table db ?table)]
    (if-let [cursor (get-in table [:table/pagination cursor-name])]
      (let [table (assoc-in table [:table/pagination :table/cursor] cursor)]
        (validate (:collection/app-db-schema table) table)
        {:db (assoc-in db [:collections id] table)
         :fx [[:dispatch [:table/resolve table]]]})
      {})))

(defevent-fx :table/prev-page
  [fx table]
  (resolve-cursor fx table :table/prev-cursor))

(defevent-fx :table/next-page
  [fx table]
  (resolve-cursor fx table :table/next-cursor))

(defevent-fx :table/per-page
  [{:keys [db]} ?table per-page]
  (let [{:table/keys [id] :as table} (utils/->table db ?table)
        db (assoc-in db [:collections id :table/pagination :table/per-page] per-page)]
    {:db db
     :fx (cond-> [[:dispatch [:table/resolve table]]]
           id (conj [:dispatch [:user/save-collection-setting id :per-page per-page]]))}))

(defevent :table/reset-cursors
  [db ?table]
  (update-in db [:collections (utils/->id ?table) :table/pagination]
             merge
             {:table/prev-cursor nil
              :table/next-cursor {:cursor/type :start :cursor/value [""]}
              :table/cursor {:cursor/type :start :cursor/value [""]}}))

(defn sortable?
  [{:table.column/keys [sort? attr]}]
  (and sort? attr))

(defn sorting-on-attr?
  [sort-attrs attr]
  (<= 0 (.indexOf sort-attrs attr)))

(defevent-fx :table/sort
  [{:keys [db]} ?table attr]
  (let [{:table/keys [id] :as table} (utils/->table db ?table)
        table (update table :table/pagination
                      (fn [{:table/keys [sort-attrs cursor] :as pagination}]
                        (if (sorting-on-attr? sort-attrs attr)
                          (let [cursor-index (.indexOf sort-attrs attr)]
                            (cond-> pagination
                              :always (assoc :table/sort-attrs (remove-elem-at sort-attrs cursor-index))
                              cursor (assoc :table/cursor (update cursor :cursor/value remove-elem-at cursor-index))))
                          (assoc pagination :table/sort-attrs
                                 (conj sort-attrs attr)))))]
    (validate (:collection/app-db-schema table) table)
    {:db (assoc-in db [:collections id] table)
     :fx [[:dispatch [:table/resolve table]]]}))

(defevent-fx :table/direction
  [{:keys [db]} ?table direction]
  (let [{:table/keys [id] :as table} (utils/->table db ?table)
        db (assoc-in db [:collections id :table/pagination :table/direction] direction)]
    {:db db
     :fx [[:dispatch [:table/resolve table]]]}))

(defsub :table/pagination
  [?table
   table <- [:table ?table]]
  (:table/pagination table))

(o/defstyled header-cell :span
  :cursor-default
  [:.indicator :invisible]
  [:&.sortable:hover
   :underline {:color "var(--accent-a11)"}
   [:.indicator :visible]]
  [:&.sorting [:.indicator :visible]]
  ([table {:table.column/keys [header attr] :as column}]
   (r/with-let [pagination (<s [:table/pagination table])]
     (let [{:table/keys [sort-attrs direction] :as pagination} @pagination]
       [:<>
        (cond-> {}
          (not (sortable? column))
          (assoc :class ["not-sortable"])

          (sortable? column)
          (assoc :on-click (fn [_] (>e [:table/sort table attr]))
                 :class ["sortable"])

          (sorting-on-attr? sort-attrs attr)
          (update :class conj "sorting"))

        header
        [c/Text {:class "indicator"
                 :color "indigo"}
         (case direction
           :asc " ↓"
           :desc " ↑")
         (when (and (sorting-on-attr? sort-attrs attr)
                    (< 1 (count sort-attrs)))
           (inc (.indexOf sort-attrs attr)))]]))))

(defsub :table/paginate?
  [?table
   table <- [:table ?table]]
  (and (:table/paginate? table)
       #_(let [{:table/keys [next-cursor prev-cursor]} (:table/pagination table)]
           (or next-cursor
               prev-cursor))))

(o/defstyled controls :div
  ;; A plain div rendered outside the table (no longer a `<tr>` with a hardcoded
  ;; colspan). Box styling lives on the inner `.controls` so the outer div
  ;; collapses to nothing for non-paginated tables (where the body is nil).
  [:.controls :flex :px-3 :py-2]
  [:.pagination :flex  :justify-center :gap-3 :ml-auto]
  ([table]
   (r/with-let [pagination (<s [:table/pagination table])
                paginate?  (<s [:table/paginate? table])]
     (when @paginate?
       (let [{:table/keys [next-cursor prev-cursor total direction per-page]} @pagination]
         [:div.controls
          [:div.pagination
           [:span "Per page: "]
           [c/SelectRoot
            {:value         (str (or per-page default-per-page))
             :onValueChange (fn [value]
                              ;; TODO use forms or at least validate
                              (let [per-page (js/parseInt value)]
                                (>e [:table/per-page table per-page])))}
            [c/SelectTrigger {:variant "soft"}]
            [c/SelectContent
             [c/SelectItem {:value "5"} 5]
             [c/SelectItem {:value "10"} 10]
             [c/SelectItem {:value "25"} 25]
             [c/SelectItem {:value "50"} 50]]]
           [:span "Direction: "]
           [c/SelectRoot
            {:value         (name (or direction :asc))
             :onValueChange (fn [value]
                              ;; TODO use forms or at least validate
                              (let [direction (keyword value)]
                                (>e [:table/direction table direction])))}
            [c/SelectTrigger {:variant "soft"}]
            [c/SelectContent
             [c/SelectItem {:value "asc"} "Asc"]
             [c/SelectItem {:value "desc"} "Desc"]]]
           [c/Button
            {:disabled (not prev-cursor)
             :on-click (fn [_] (>e [:table/prev-page table]))
             :variant "soft"}
            "< Prev"]
           [c/Button
            {:disabled (not next-cursor)
             :on-click (fn [_] (>e [:table/next-page table]))
             :variant "soft"}
            "Next >"]
           [:span "Total: " total]]])))))
